import ufeApi from 'services/api/ufeApi';
import Empty from 'constants/empty';
import { getOrCreateAbTestUuid } from 'utils/localStorage/AbTestUuid';

const path = '/gway/v1/p13n/puwlo/recap/anonymous';

function normalizeRecapAbTestRow(row) {
    if (!row) {
        return null;
    }

    const p13n = row.p13n;

    if (!p13n) {
        return null;
    }

    const isRecapEnabled = (p13n.isRecapEnabled ?? p13n.isRecap) === true;

    return {
        context: p13n.context ?? row.context,
        deviceId: p13n.deviceId,
        abTestId: p13n.abTestId,
        endDate: p13n.abTestEndDate ?? p13n.endDate,
        isControl: p13n.isControl === true,
        isDefault: p13n.isDefault === true,
        isAbTest: p13n.isAbTest === true,
        isRecap: isRecapEnabled
    };
}

/**
 * Fetches anonymous recap AB test data from the PUWLO endpoint.
 * Uses a persistent session UUID for consistent anonymous user bucketing.
 * @param {object} params
 * @param {string} params.channel - Request channel (e.g. 'web')
 * @param {string} params.country - Two-letter country code (e.g. 'US')
 * @param {string} params.language - Language code (e.g. 'en')
 * @param {string} params.contextId - CMS entry ID for the recap AB test
 * @param {string} params.abTestEndDate - ISO 8601 expiry date for the AB test UUID
 * @returns {Promise<object|null>} Normalized recap AB test row, or null on failure
 */
function getP13nRecapABTestData({
    channel, country, language, contextId, abTestEndDate
}) {
    // Generate or retrieve persistent Session-ID (UUID) for this AB test context
    // UFE uses Session-ID, mobile apps use Device-ID
    // This ensures consistent bucketing for the anonymous user across sessions
    const deviceId = getOrCreateAbTestUuid(contextId, abTestEndDate);

    if (!deviceId) {
        Sephora.logger.error('PUWLO RTCO RECAP: Failed to generate session ID for AB test');

        return null;
    }

    const locale = `${language}-${country}`;
    // API expects "deviceId" parameter but UFE sends Session-ID value
    const queryParams = `?deviceId=${encodeURIComponent(deviceId)}&contextId=${encodeURIComponent(contextId)}&channel=${encodeURIComponent(channel)}&locale=${encodeURIComponent(locale)}`;

    const url = `${path}${queryParams}`;

    return ufeApi
        .makeRequest(url, { method: 'GET' })
        .then(({ data = Empty.Array, errorCode, message, status }) => {
            // Check for API-level errors (errorCode from ufeApi)
            if (errorCode) {
                Sephora.logger.warn(`PUWLO RTCO RECAP: API error - ${message || 'Unknown error'}`);

                return null;
            }

            // Check for business-level errors from PUWLO recap anonymous
            // status: 0 = success, status: 1 = error; omit status when endpoint returns data only
            if (status != null && status !== 0) {
                Sephora.logger.warn(`PUWLO RTCO RECAP: PUWLO recap anonymous error (status ${status}) - ${message || 'Unknown error'}`);

                return null;
            }

            // Extract first data row from response array
            const firstRow = Array.isArray(data) ? data[0] : data;

            if (!firstRow) {
                Sephora.logger.warn('PUWLO RTCO RECAP: No data returned from PUWLO recap anonymous');

                return null;
            }

            return normalizeRecapAbTestRow(firstRow);
        })
        .catch(error => {
            Sephora.logger.error('PUWLO RTCO RECAP: Failed to fetch data', error);

            return null;
        });
}

export default getP13nRecapABTestData;
